/**
 * Get display name for filter types used in result views
 */
export const getFilterDisplayName = (filterBy: string): string => {
    switch (filterBy) {
        case 'all':
            return 'All Results';
        case 'normal':
        case 'within-range':
            return 'Within Range';
        case 'abnormal':
            return 'Abnormal Results';
        case 'borderline':
        case 'needs-attention':
            return 'Needs Attention';
        case 'critical':
        case 'high-priority':
            return 'High Priority';
        case 'baseline':
            return 'Baseline Health';
        case 'thyroid':
            return 'Thyroid Health';
        case 'allergy':
            return 'Allergy Tests';
        default:
            return 'Selected Filter';
    }
};

/**
 * Generate mock historical data for biomarker trend visualization
 */
export const generateMockHistoricalData = (currentValue: number | null) => {
    // Non-numeric results have no base value to extrapolate a mock series from.
    if (currentValue === null) return [];
    const baseValue = parseFloat(String(currentValue));
    if (isNaN(baseValue)) return [];

    const roundToOne = (num: number) => Math.round(num * 10) / 10;

    return [
        {date: new Date('2024-12-01'), value: roundToOne(baseValue * 0.85)},
        {date: new Date('2025-02-01'), value: roundToOne(baseValue * 0.92)},
        {date: new Date('2025-06-01'), value: roundToOne(baseValue * 0.88)},
        {date: new Date('2025-07-01'), value: roundToOne(baseValue * 1.02)},
    ];
};

/**
 * Collect all results for a specific test name from patient data
 */
type TestResultRow = {
    test_name?: string;
    result?: string | number | null;
    statusDate?: string | Date | null;
    amendedDate?: string | Date | null;
    createdDate?: string | Date | null;
};

type TestWithResults = {
    id?: number;
    createdDate?: string | Date | null;
    amendedDate?: string | Date | null;
    results?: TestResultRow[];
};

export const collectTestResults = (allPatientData: TestWithResults[], testName: string) => {
    const allResults: Array<TestResultRow & {orderId?: number}> = [];

    // Normalize the search test name for case-insensitive matching
    const normalizedSearchName = testName.toLowerCase().trim();

    allPatientData.forEach((test) => {
        if (test.results) {
            test.results.forEach((result) => {
                // Normalize result test name for comparison
                const resultTestName = result.test_name || '';
                const normalizedResultName = resultTestName.toLowerCase().trim();

                // Check for exact match (case-insensitive)
                const isExactMatch = normalizedResultName === normalizedSearchName;

                // Check for compound test name match (e.g., "Panel Name; Test Name")
                const isCompoundMatch =
                    resultTestName.includes('; ') &&
                    resultTestName.split('; ')[1]?.toLowerCase().trim() === normalizedSearchName;

                if (isExactMatch || isCompoundMatch) {
                    allResults.push({
                        ...result,
                        orderId: test.id,
                        amendedDate: test.amendedDate,
                        createdDate: test.createdDate,
                    });
                }
            });
        }
    });

    return allResults;
};

export type TestingHistoryPoint = {
    date: string | Date;
    value: number | null;
    label?: string;
    orderId?: number;
};

export type TestingHistoryChartPoint = {
    name: string;
    value: number | null;
    dummy?: boolean;
};

const formatHistoryLabel = (rawDate: string | Date | null | undefined): string => {
    const date = new Date(rawDate ?? '');
    if (Number.isNaN(date.getTime())) return '';
    // Month + apostrophe-year (e.g. "Jan '26") to match the v5 design's
    // Result History axis labels.
    const month = date.toLocaleDateString('en-US', {month: 'short'});
    const year = date.toLocaleDateString('en-US', {year: '2-digit'});
    return `${month} '${year}`;
};

const getResultTimestamp = (result: TestResultRow): number => {
    const rawDate = result.statusDate || result.amendedDate || result.createdDate;
    const ms = rawDate ? new Date(rawDate).getTime() : 0;
    return Number.isFinite(ms) ? ms : 0;
};

export const buildHistoryPointsFromResults = (
    allResults: Array<TestResultRow & {orderId?: number}>
): TestingHistoryPoint[] => {
    const resultsByOrder = new Map<string, TestResultRow & {orderId?: number}>();

    allResults.forEach((result) => {
        const orderId = Number(result.orderId);
        const key =
            Number.isFinite(orderId) && orderId > 0
                ? `order:${orderId}`
                : `fallback:${result.test_name}:${result.statusDate || result.createdDate || ''}:${result.result}`;

        const existing = resultsByOrder.get(key);
        if (!existing || getResultTimestamp(result) > getResultTimestamp(existing)) {
            resultsByOrder.set(key, result);
        }
    });

    return Array.from(resultsByOrder.values())
        .sort((a, b) => getResultTimestamp(a) - getResultTimestamp(b))
        .map((result) => {
            const date = result.statusDate || result.amendedDate || result.createdDate || '';
            const rawResult = result.result ?? '';
            const parsedValue =
                typeof rawResult === 'number' ? rawResult : parseFloat(String(rawResult));
            return {
                date,
                value: Number.isFinite(parsedValue) ? parsedValue : null,
                label: formatHistoryLabel(date),
                orderId: result.orderId,
            };
        });
};

export const buildTestingHistoryChartData = (options: {
    history: TestingHistoryPoint[];
    currentValue: number | null;
    currentDate?: string | Date | null;
    maxHistoryPoints?: number;
}): TestingHistoryChartPoint[] => {
    const {history, currentValue, currentDate, maxHistoryPoints = 3} = options;
    const trimmedHistory = history.slice(-maxHistoryPoints);
    const lastHistory = trimmedHistory[trimmedHistory.length - 1];
    const lastHistoryLabel = lastHistory ? lastHistory.label || formatHistoryLabel(lastHistory.date) : '';
    const currentLabel = formatHistoryLabel(currentDate) || lastHistoryLabel;

    if (trimmedHistory.length >= 1) {
        const points: TestingHistoryChartPoint[] = trimmedHistory.map((point) => ({
            name: point.label || formatHistoryLabel(point.date),
            value: point.value,
        }));

        // Append current value only when its date isn't already in the
        // series. The previous check only compared against the LAST
        // point — that broke for historical-result pages where the
        // current view's date sits in the middle of the timeline,
        // producing a duplicate dot at an out-of-order x-axis position.
        const hasMatchingDate = currentLabel && points.some((p) => p.name === currentLabel);
        if (currentLabel && !hasMatchingDate) {
            points.push({name: currentLabel, value: currentValue});
        }

        // Single point with no separate current → show as lone dot
        if (points.length <= 1) {
            return [
                {name: '', value: null, dummy: true},
                {name: points[0]?.name || currentLabel, value: currentValue},
            ];
        }

        return points;
    }

    return [
        {name: '', value: null, dummy: true},
        {
            name: currentLabel,
            value: currentValue,
        },
    ];
};

// ── Design-grid chart data ────────────────────────────────────────────
// Shared by the v5 "design" charts (Lab Results "Result History" and the
// Health Overview biomarker mini-charts): real points get numeric x =
// 0..N-1 and two flat "edge" points sit a short fraction (DESIGN_LEAD) of
// a gap past the ends, so the area + line run to the left/right borders
// with only a short lead-in while the markers (and the current dot) stay
// inset from the frame.

export const DESIGN_LEAD = 0.3;

export interface DesignGridPoint {
    name: string;
    value: number | null;
    x: number;
    edge?: boolean;
    current?: boolean;
}

export const buildDesignGridData = (
    points: Array<{name?: string; value: number | null; dummy?: boolean}>
): {data: DesignGridPoint[]; domain: [number, number]} => {
    const reals = points.filter((p) => !p.dummy && p.value !== null);
    const n = reals.length;
    if (n === 0) return {data: [], domain: [0, 1]};
    const lead = n > 1 ? DESIGN_LEAD : 0.5;
    const lastX = n - 1 + lead;
    const data: DesignGridPoint[] = [
        {name: '__edgeL', x: -lead, value: reals[0].value, edge: true},
        ...reals.map((p, i) => ({name: p.name ?? '', value: p.value, x: i, current: i === n - 1})),
        {name: '__edgeR', x: lastX, value: reals[n - 1].value, edge: true},
    ];
    return {data, domain: [-lead, lastX]};
};

/**
 * Get historical data for a biomarker
 * Returns real data for subscribers with historical tests, mock data otherwise
 */
export const getHistoricalData = (
    testName: string,
    currentValue: number | null,
    subscription: boolean,
    allPatientData: TestWithResults[] | null,
    excludeMostRecent: boolean = false
) => {
    if (subscription && allPatientData && allPatientData.length > 0) {
        const allResults = collectTestResults(allPatientData, testName);
        const historyPoints = buildHistoryPointsFromResults(allResults);

        // Show history if we have at least 1 historical data point
        // (The BiomarkerChartCard will add the current value, so 1 historical + current = 2 points on chart)
        if (historyPoints.length >= 1) {
            // Exclude the most recent result if requested (it will be shown as "Current")
            // BUT: Keep it if it's the only result we have (need at least 1 point for history)
            const shouldExclude = excludeMostRecent && historyPoints.length > 1;
            return shouldExclude ? historyPoints.slice(0, -1) : historyPoints;
        }
    }

    // Fall back to mock data for non-subscribers or when no test data
    return generateMockHistoricalData(currentValue);
};
